import React, { useState } from 'react';
import { 
  FileText, Table, Satellite, ShieldCheck, Download, 
  Sparkles, CheckCircle2, AlertTriangle, Eye, ArrowRight, Layers,
  FileSpreadsheet, Bookmark, Award, Check, ExternalLink, Printer,
  Calendar, MapPin, Hash, UserCheck, AlertCircle, ChevronDown, ChevronUp, Copy,
  Coins, Scale, Compass, CheckCircle
} from 'lucide-react';
import { Language } from '../utils/i18n';

interface InteractiveShowcasePreviewProps {
  language: Language;
  onSelectAgent?: (agentId: string) => void;
}

export default function InteractiveShowcasePreview({
  language,
  onSelectAgent
}: InteractiveShowcasePreviewProps) {
  const [activeDocType, setActiveDocType] = useState<'contract' | 'estimate' | 'satellite' | 'tax_cbam'>('contract');
  const [activeSection, setActiveSection] = useState<'summary' | 'details' | 'calculations' | 'action_plan'>('details');
  const [copied, setCopied] = useState(false);

  // Normalize language helper
  const lang = (['uk', 'pl', 'de', 'fr', 'it', 'es', 'pt', 'sv', 'en'].includes(language) ? language : 'en') as 
    'uk' | 'pl' | 'de' | 'fr' | 'it' | 'es' | 'pt' | 'sv' | 'en';

  const isUk = lang === 'uk';
  const isPl = lang === 'pl';
  const isDe = lang === 'de';
  const isFr = lang === 'fr';

  // Localized UI Labels
  const ui = {
    title: {
      uk: '🔬 ЗРАЗКИ ПОВНОФОРМАТНИХ ОФІЦІЙНИХ ЗВІТІВ ШІ (FULL ARTIFACT PROOFS)',
      pl: '🔬 PEŁNOFORMATOWE WZORY OFICJALNYCH RAPORTÓW AI (DOKUMENTY DOCX/XLSX)',
      de: '🔬 VOLLSTÄNDIGE MUSTER OFFIZIELLER KI-BERICHTE (WORD / EXCEL / GIS)',
      fr: '🔬 EXEMPLES COMPLETS DE RAPPORTS OFFICIELS IA (FORMATS PRO DOCX/XLSX)',
      it: '🔬 ESEMPI COMPLETI DI REPORT UFFICIALI GENERATI DALL\'IA (DOCX/XLSX)',
      es: '🔬 MUESTRAS COMPLETAS DE INFORMES OFICIALES DE IA (DOCX/XLSX/GIS)',
      pt: '🔬 EXEMPLOS COMPLETOS DE RELATÓRIOS OFICIAIS IA (DOCX/XLSX/SIG)',
      sv: '🔬 FULLSTÄNDIGA PROV PÅ OFFICIELLA AI-RAPPORTER (WORD / EXCEL / GIS)',
      en: '🔬 FULL-FORMAT OFFICIAL AI REPORT SHOWCASE (REAL DOCX / XLSX ARTIFACTS)'
    }[lang],
    subtitle: {
      uk: 'Ознайомтесь з реальною структурою 10–15 сторінкових аудиторських висновків за ДСТУ, кошторисів РЕКН та супутникових паспортів до входу в систему:',
      pl: 'Zobacz autentyczną strukturę wielostronicowych audytów prawnych, kosztorysów KNR i paszportów satelitarnych przed logowaniem:',
      de: 'Prüfen Sie den vollständigen Aufbau 10-15-seitiger Rechtsgutachten, DIN-Baukosten und Satellitenberichte vor der Anmeldung:',
      fr: 'Consultez la structure réelle des audits juridiques complets, devis BTP et rapports satellites avant la connexion :',
      it: 'Consulta la struttura reale di audit legali completi, preventivi edilizi e report satellitari prima dell\'accesso:',
      es: 'Consulte la estructura real de auditorías legales completas, presupuestos de construcción y pasaportes satelitales antes de iniciar sesión:',
      pt: 'Consulte a estrutura real de auditorias jurídicas completas, orçamentos de obras e relatórios de satélite antes de entrar:',
      sv: 'Granska den faktiska strukturen för fullständiga juridiska granskningar, kalkylblad och satellitpass före inloggning:',
      en: 'Inspect the actual multi-page structure of formal legal due diligence, formulaic BOQs, and satellite passports prior to signing in:'
    }[lang],
    badgeGenerated: {
      uk: '⚡ 15 СЕК НА ГЕНЕРАЦІЮ • 100% ТОЧНІСТЬ',
      pl: '⚡ 15 SEK NA RAPORT • 100% PRECYZJI',
      de: '⚡ 15 SEKUNDEN GENERIERUNG • 100% PRÄZISION',
      fr: '⚡ 15 SEC DE GÉNÉRATION • PRÉCISION 100%',
      it: '⚡ 15 SEC PER IL REPORT • 100% PRECISIONE',
      es: '⚡ 15 SEG PARA EL INFORME • 100% PRECISIÓN',
      pt: '⚡ 15 SEG PARA O RELATÓRIO • 100% PRECISÃO',
      sv: '⚡ 15 SEK GENERERING • 100% PRECISION',
      en: '⚡ 15 SEC GENERATION • 100% PRECISION'
    }[lang],
    tabContract: {
      uk: '📄 Аудит Договору',
      pl: '📄 Audyt Umowy B2B',
      de: '📄 Vertragsprüfung',
      fr: '📄 Audit de Contrat',
      it: '📄 Audit Contrattuale',
      es: '📄 Auditoría de Contrato',
      pt: '📄 Auditoria de Contrato',
      sv: '📄 Avtalsgranskning',
      en: '📄 Contract Due Diligence'
    }[lang],
    tabEstimate: {
      uk: '🏗️ Кошторис Будівництва',
      pl: '🏗️ Kosztorys KNR',
      de: '🏗️ Baukosten DIN 276',
      fr: '🏗️ Devis & Métré BTP',
      it: '🏗️ Computo Metrico',
      es: '🏗️ Presupuesto de Obra',
      pt: '🏗️ Orçamento de Obras',
      sv: '🏗️ Byggkalkyl & BOQ',
      en: '🏗️ Construction BOQ'
    }[lang],
    tabSat: {
      uk: '🛰️ Паспорт Поля (NDVI)',
      pl: '🛰️ Paszport Pola (GIS)',
      de: '🛰️ Satelliten-Feldpass',
      fr: '🛰️ Passeport Parcelle (GIS)',
      it: '🛰️ Passaporto Campo (NDVI)',
      es: '🛰️ Pasaporte Parcela (NDVI)',
      pt: '🛰️ Passaporte Talhão (SIG)',
      sv: '🛰️ Satellitpass (NDVI)',
      en: '🛰️ Satellite Field (NDVI)'
    }[lang],
    tabTax: {
      uk: '🏛️ Податки & CBAM ЄС',
      pl: '🇵🇱 KSeF FA_VAT & JPK_V7',
      de: '🏛️ EU CBAM & Steuern',
      fr: '🏛️ Taxe CBAM & TVA UE',
      it: '🏛️ CBAM UE & IVA',
      es: '🏛️ CBAM UE & Fiscalidad',
      pt: '🏛️ CBAM UE & Fiscal',
      sv: '🏛️ EU CBAM & Moms',
      en: '🏛️ EU CBAM & Tax VAT'
    }[lang],
    sectionSummary: {
      uk: '1. Резюме та Статус',
      pl: '1. Podsumowanie & Status',
      de: '1. Zusammenfassung & Status',
      fr: '1. Résumé & Statut',
      it: '1. Sintesi & Stato',
      es: '1. Resumen & Estado',
      pt: '1. Resumo & Status',
      sv: '1. Sammanfattning & Status',
      en: '1. Executive Summary'
    }[lang],
    sectionDetails: {
      uk: '2. Матриця Ризиків та Пунктів',
      pl: '2. Matryca Ryzyk i Klauzul',
      de: '2. Risiko- & Klauselmatrix',
      fr: '2. Matrice des Risques & Clauses',
      it: '2. Matrice Rischi & Clausole',
      es: '2. Matriz de Riesgos y Cláusulas',
      pt: '2. Matriz de Riscos e Cláusulas',
      sv: '2. Risk- & Klauselmatris',
      en: '2. Risk & Clause Matrix'
    }[lang],
    sectionCalculations: {
      uk: '3. Таблиці та Розрахунки',
      pl: '3. Tabele & Obliczenia',
      de: '3. Tabellen & Berechnungen',
      fr: '3. Tableaux & Calculs',
      it: '3. Tabelle & Calcoli',
      es: '3. Tablas y Cálculos',
      pt: '3. Tabelas e Cálculos',
      sv: '3. Tabeller & Beräkningar',
      en: '3. Tables & Calculations'
    }[lang],
    sectionPlan: {
      uk: '4. План Дій для Керівника',
      pl: '4. Plan Działań dla Zarządu',
      de: '4. Handlungsempfehlungen',
      fr: '4. Plan d\'Action de Direction',
      it: '4. Piano d\'Azione Direzionale',
      es: '4. Plan de Acción Directiva',
      pt: '4. Plano de Ação Diretiva',
      sv: '4. Handlingsplan för Ledning',
      en: '4. Action Plan for Leadership'
    }[lang],
    freeRunBanner: {
      uk: '🎁 1 безкоштовний запуск будь-якого модуля нараховується автоматично після входу',
      pl: '🎁 1 darmowe uruchomienie dowolnego modułu jest aktywne zaraz po zalogowaniu',
      de: '🎁 1 kostenloser Probelauf jedes Moduls wird nach der Anmeldung freigeschaltet',
      fr: '🎁 1 essai complet gratuit pour tout module est accordé immédiatement après connexion',
      it: '🎁 1 prova gratuita completa per qualsiasi modulo è attiva subito dopo l\'accesso',
      es: '🎁 1 ejecución completa gratuita de cualquier módulo se activa tras iniciar sesión',
      pt: '🎁 1 execução completa gratuita de qualquer módulo fica ativa logo após o login',
      sv: '🎁 1 gratis fullständig körning av valfri modul aktiveras direkt efter inloggning',
      en: '🎁 1 free full run of any agent or calculator is activated immediately upon signing in'
    }[lang],
    tryNowBtn: {
      uk: '🚀 Спробувати 1-й запуск безкоштовно',
      pl: '🚀 Uruchom 1. raz bezpłatnie',
      de: '🚀 Kostenlos testen',
      fr: '🚀 Essayer gratuitement',
      it: '🚀 Prova gratuita',
      es: '🚀 Probar gratis',
      pt: '🚀 Testar grátis',
      sv: '🚀 Testa gratis',
      en: '🚀 Try 1st Run For Free'
    }[lang]
  };

  // FULL MULTI-LINGUAL REPORT CONTENT DATABASE
  const reportData = {
    contract: {
      title: {
        uk: 'ОФІЦІЙНИЙ ЗВІТ ПРАВОВОГО АУДИТУ ТА ВЕРИФІКАЦІЇ ДОГОВОРУ № АУД-2026/08',
        pl: 'OFICJALNY RAPORT AUDYTU PRAWNEGO I WERYFIKACJI UMOWY NR AUD-PL/2026',
        de: 'OFFIZIELLER RECHTSGUTACHTEN- & VERTRAGSPRÜFUNGSBERICHT NR. AUD-DE/2026',
        fr: 'RAPPORT OFFICIEL D\'AUDIT JURIDIQUE ET DE CONFORMITÉ CONTRACTUELLE N° AUD-FR/2026',
        it: 'REPORT UFFICIALE DI AUDIT LEGALE E CONFORMITÀ CONTRATTUALE N. AUD-IT/2026',
        es: 'INFORME OFICIAL DE AUDITORÍA LEGAL Y REVISIÓN CONTRACTUAL N.º AUD-ES/2026',
        pt: 'RELATÓRIO OFICIAL DE AUDITORIA JURÍDICA E CONFORMIDADE CONTRATUAL N.º AUD-PT/2026',
        sv: 'OFFICIELL JURIDISK GRANSKNINGSRAPPORT FÖR AVTAL NR AUD-SE/2026',
        en: 'OFFICIAL LEGAL DUE DILIGENCE & CONTRACT AUDIT REPORT #AUD-2026/EU'
      }[lang] || 'OFFICIAL LEGAL DUE DILIGENCE & CONTRACT AUDIT REPORT #AUD-2026/EU',
      standard: {
        uk: 'ДСТУ 4163:2020 • ЦКУ ст. 759-786 (Оренда)',
        pl: 'Kodeks cywilny art. 659-692 • KSH • RODO',
        de: 'BGB §§ 535-580a • HGB • AGB-Recht • DSGVO',
        fr: 'Code civil art. 1708-1778 • Code de commerce • RGPD',
        en: 'Rome I Regulation • CISG • Standard Commercial Code'
      }[lang] || 'Rome I Regulation • CISG • Standard Commercial Code',
      volume: {
        uk: '14 сторінок • 48 перевірених пунктів',
        pl: '14 stron • 48 zbadanych klauzul',
        de: '14 Seiten • 48 geprüfte Klauseln',
        fr: '14 pages • 48 clauses analysées',
        en: '14 pages • 48 audited clauses'
      }[lang] || '14 pages • 48 audited clauses',
      summaryText: {
        uk: 'Проведено комплексний правовий та комерційний аудит договору оренди приміщення № 14-ОР/2026. Виявлено 3 критичні умови, які покладають на Орендаря односторонні штрафні санкції у розмірі 20% та надають Орендодавцю право розірвання договору в одноденний термін. Запропоновано збалансовані юридичні редакції з посиланнями на ст. 759–786 ЦКУ та норми діловодства ДСТУ 4163:2020.',
        pl: 'Przeprowadzono kompleksowy audyt prawny i handlowy umowy najmu lokalu komercyjnego nr 14-NL/2026. Zidentyfikowano 3 klauzule abuzywne nakładające rażąco wygórowane kary umowne (20%) oraz prawo do natychmiastowego wypowiedzenia umowy bez okresu naprawczego. Przygotowano zbalansowane brzmienie klauzul w oparciu o art. 659-692 Kodeksu cywilnego oraz przepisy RODO.',
        de: 'Umfassende rechtliche und wirtschaftliche Prüfung des Gewerberaummietvertrags Nr. 14-GM/2026 durchgeführt. Es wurden 3 unzulässige AGB-Klauseln identifiziert (überhöhte Vertragsstrafe von 20% und fristloses Kündigungsrecht ohne Abmahnung). Ausgewogene Neufassungen gemäß §§ 305-310 BGB und BGH-Rechtsprechung wurden formuliert.',
        fr: 'Audit juridique et financier complet du bail commercial n° 14-BC/2026 réalisé. Identification de 3 clauses abusives prévoyant des pénalités excessives de 20% et une résiliation unilatérale sans mise en demeure préalable. Rédaction de clauses équilibrées conformes aux articles L145-1 et suivants du Code de commerce.',
        en: 'Conducted a thorough legal, financial, and commercial risk audit of Commercial Lease Agreement #14-CL/2026. Identified 3 high-risk clauses imposing one-sided liquidated damages (20%) and unilateral termination without default cure notice. Drafted market-standard balancing amendments aligned with governing commercial contract principles and international statutory compliance.'
      }[lang] || 'Conducted a thorough legal, financial, and commercial risk audit of Commercial Lease Agreement #14-CL/2026.',
      rows: [
        {
          clause: { uk: 'п. 6.4 (Штрафи)', pl: 'Art. 6.4 (Kary umowne)', de: '§ 6.4 (Vertragsstrafe)', fr: 'Art. 6.4 (Pénalités)', en: 'Clause 6.4 (Penalties)' }[lang] || 'Clause 6.4 (Penalties)',
          initial: {
            uk: '«За прострочення оплати Орендар сплачує штраф 20% від суми боргу + пеню 1% за кожен день».',
            pl: '«W przypadku opóźnienia w płatności Najemca zapłaci karę umowną 20% kwoty długu + odsetki 1% za każdy dzień».',
            de: '«Bei Zahlungsverzug zahlt der Mieter eine Vertragsstrafe von 20% zzgl. 1% Verzugszinsen pro Kalendertag».',
            fr: '«En cas de retard de paiement, le Preneur versera une pénalité forfaitaire de 20% plus 1% par jour de retard».',
            en: '«In case of payment delay, Tenant shall pay a 20% lump-sum penalty plus 1% interest per day of delay».'
          }[lang] || '«In case of payment delay, Tenant shall pay a 20% lump-sum penalty plus 1% interest per day of delay».',
          analysis: {
            uk: 'Порушення ст. 61 Конституції та ст. 549 ЦКУ: подвійна відповідальність за 1 порушення.',
            pl: 'Klauzula abuzywna: rażące wygórowanie kary umownej (art. 484 § 2 KC) i zakaz kumulacji sankcji.',
            de: 'Unwirksam nach § 307 BGB wegen unangemessener Benachteiligung und Zinswucher.',
            fr: 'Clause pénale manifestement excessive susceptible de réduction judiciaire (art. 1231-5 C. civ.).',
            en: 'Unenforceable penalty clause violating standard liquidated damages caps under applicable commercial law.'
          }[lang] || 'Unenforceable penalty clause violating standard liquidated damages caps under applicable commercial law.',
          redline: {
            uk: '«За порушення строків оплати сплачується пеня у розмірі подвійної облікової ставки НБУ від суми боргу».',
            pl: '«W przypadku opóźnienia naliczane są ustawowe odsetki za opóźnienie w transakcjach handlowych».',
            de: '«Bei Zahlungsverzug gelten die gesetzlichen Verzugszinsen gemäß § 288 Abs. 2 BGB (9 Prozentpunkte über Basiszinssatz)».',
            fr: '«En cas de retard, il sera fait application du taux d\'intérêt légal majoré conformément aux règles de droit commun».',
            en: '«In case of payment delay, standard statutory commercial default interest shall apply capped at 0.05% per day».'
          }[lang] || '«In case of payment delay, standard statutory commercial default interest shall apply capped at 0.05% per day».'
        },
        {
          clause: { uk: 'п. 9.2 (Розірвання)', pl: 'Art. 9.2 (Wypowiedzenie)', de: '§ 9.2 (Kündigung)', fr: 'Art. 9.2 (Résiliation)', en: 'Clause 9.2 (Termination)' }[lang] || 'Clause 9.2 (Termination)',
          initial: {
            uk: '«Орендодавець має право в односторонньому порядку розірвати договір, повідомивши за 3 календарні дні».',
            pl: '«Wynajmujący ma prawo jednostronnie rozwiązać umowę ze skutkiem natychmiastowym z 3-dniowym uprzedzeniem».',
            de: '«Der Vermieter ist berechtigt, den Vertrag einseitig mit einer Frist von 3 Kalendertagen zu kündigen».',
            fr: '«Le Bailleur pourra résilier unilatéralement le contrat avec un préavis de 3 jours calendaires».',
            en: '«Landlord may terminate this agreement unilaterally upon providing 3 calendar days written notice».'
          }[lang] || '«Landlord may terminate this agreement unilaterally upon providing 3 calendar days written notice».',
          analysis: {
            uk: 'Невідповідність ст. 782 ЦКУ: необхідне письмове попередження за 30 днів із наданням строку на усунення.',
            pl: 'Naruszenie art. 687 KC: obowiązek wyznaczenia dodatkowego miesięcznego terminu na zapłatę.',
            de: 'Verstoß gegen § 543 Abs. 3 BGB: Fristlose Kündigung erfordert zwingend vorherige Abmahnung.',
            fr: 'Non-respect du formalisme de la clause résolutoire et du délai légal de mise en demeure d\'un mois.',
            en: 'Failure to provide mandatory 30-day cure period required for commercial default remedies.'
          }[lang] || 'Failure to provide mandatory 30-day cure period required for commercial default remedies.',
          redline: {
            uk: '«Розірвання можливе виключно після направлення письмової вимоги з наданням 30-денного строку на усунення недоліків».',
            pl: '«Rozwiązanie umowy wymaga pisemnego upomnienia z wyznaczeniem co najmniej 30-dniowego terminu naprawczego».',
            de: '«Kündigung ist erst nach schriftlicher Abmahnung mit einer Abhilfefrist von mindestens 30 Tagen zulässig».',
            fr: '«La résiliation ne prendra effet qu\'après commandement de payer demeuré infructueux pendant 30 jours».',
            en: '«Termination requires prior formal written notice with a mandatory 30-calendar-day cure period».'
          }[lang] || '«Termination requires prior formal written notice with a mandatory 30-calendar-day cure period».'
        }
      ],
      calcGrid: [
        { label: { uk: 'Потенційний ризик штрафів:', pl: 'Ekspozycja na kary:', en: 'Penalty Risk Exposure:' }[lang] || 'Penalty Risk Exposure:', val: isUk ? '120 000,00 ₴' : isPl ? '24 000 PLN' : '€5,000 / $5,000', col: 'text-red-400' },
        { label: { uk: 'Зменшення відповідальності:', pl: 'Zmniejszona odpowiedzialność:', en: 'Negotiated Liability Cap:' }[lang] || 'Negotiated Liability Cap:', val: isUk ? '12 000,00 ₴' : isPl ? '2 400 PLN' : '€500 / $500', col: 'text-emerald-400' },
        { label: { uk: 'Заощаджено на юристах:', pl: 'Oszczędność na obsłudze prawnej:', en: 'Legal Cost Avoidance:' }[lang] || 'Legal Cost Avoidance:', val: isUk ? '15 000,00 ₴' : isPl ? '1 800 PLN' : '€400 / $450', col: 'text-blue-400' },
        { label: { uk: 'Захист від суду:', pl: 'Ochrona przed sporem:', en: 'Litigation Protection:' }[lang] || 'Litigation Protection:', val: '100% Guaranteed', col: 'text-purple-400' }
      ],
      actionPlan: {
        uk: [
          'Направити Орендодавцю офіційний протокол розбіжностей до статей 6.4, 9.2 та 12.1.',
          'Зафіксувати обмеження сукупної відповідальності на рівні 10% від річної орендної плати.',
          'Внести реквізити банківського рахунку зі стандартом IBAN та кодом ЄДРПОУ відповідно до ДСТУ 4163:2020.',
          'Зберегти верифікований звіт в електронному реєстрі комплаєнсу підприємства.'
        ],
        pl: [
          'Wysłać Wynajmującemu oficjalny protokół rozbieżności do klauzul 6.4, 9.2 oraz 12.1.',
          'Ograniczyć łączną odpowiedzialność odszkodowawczą do maksymalnie 10% rocznego czynszu.',
          'Zweryfikować rachunek bankowy Wynajmującego na Białej Liście Podatników VAT Ministerstwa Finansów.',
          'Zarchiwizować podpisany cyfrowo raport audytu w systemie compliance spółki.'
        ],
        de: [
          'Übersendung des offiziellen Verhandlungsprotokolls zu den Klauseln 6.4, 9.2 und 12.1 an den Vermieter.',
          'Haftungsbegrenzung auf maximal 10% der jährlichen Nettokaltmiete vereinbaren.',
          'Überprüfung der Umsatzsteuer-Identifikationsnummer (USt-IdNr.) im VIES-System.',
          'Digitale Archivierung des QES-gezeichneten Prüfberichts im Compliance-Management-System.'
        ],
        fr: [
          'Transmettre au Bailleur le protocole de désaccord portant sur les articles 6.4, 9.2 et 12.1.',
          'Plafonner la responsabilité globale du Preneur à 10% du loyer annuel hors taxes.',
          'Contrôler le numéro de TVA intracommunautaire et les coordonnées bancaires du Bailleur.',
          'Archiver le rapport d\'audit signé numériquement dans le registre de conformité de l\'entreprise.'
        ],
        en: [
          'Issue formal Statement of Departures / Redline Notice regarding Clauses 6.4, 9.2, and 12.1.',
          'Cap aggregate liability and indemnity obligations at 10% of the annual contract value.',
          'Verify corporate counterparty tax credentials and banking details via official statutory registries.',
          'Store the verified QES-signed due diligence report in the corporate digital compliance archive.'
        ]
      }[lang] || [
        'Issue formal Statement of Departures / Redline Notice regarding Clauses 6.4, 9.2, and 12.1.',
        'Cap aggregate liability and indemnity obligations at 10% of the annual contract value.',
        'Verify corporate counterparty tax credentials and banking details via official statutory registries.',
        'Store the verified QES-signed due diligence report in the corporate digital compliance archive.'
      ]
    },

    estimate: {
      title: {
        uk: 'ЗВЕДЕНИЙ ЛОКАЛЬНИЙ КОШТОРИС № 02-01-04 (РЕКН / КНУ-2021/2026)',
        pl: 'KOSZTORYS INWESTORSKI I PROTOKÓŁ ODBIORU ROBÓT NR KNR-2026/PL',
        de: 'BAUKOSTENBERECHNUNG NACH DIN 276 & LEISTUNGSVERZEICHNIS (LV)',
        fr: 'DEVIS QUANTITATIF ESTIMATIF (DQE) & DÉCOMPTE GÉNÉRAL BTP',
        it: 'COMPUTO METRICO ESTIMATIVO E STATO AVANZAMENTO LAVORI (SAL)',
        es: 'PRESUPUESTO DE EJECUCIÓN MATERIAL Y CERTIFICACIÓN DE OBRA',
        pt: 'ORÇAMENTO DISCRIMINADO E AUTO DE MEDIÇÃO DE OBRAS',
        sv: 'DETALJERAD BYGGKALKYL & MÄTBREV ENLIGT AMA/BOQ',
        en: 'BILL OF QUANTITIES (BOQ) & FIDIC INTERIM PAYMENT CERTIFICATE #IPC-2026'
      }[lang] || 'BILL OF QUANTITIES (BOQ) & FIDIC INTERIM PAYMENT CERTIFICATE #IPC-2026',
      standard: {
        uk: 'РЕКН / КНУ-2021 / ДБН А.3.1-5:2016',
        pl: 'KNR / KNNR / Cenniki SEKOCENBUD 2026',
        de: 'DIN 276 / VOB/B / HOAI 2026',
        fr: 'Normes NF P03-001 / CCTG Travaux',
        en: 'FIDIC Red & Yellow Books / Eurocodes EN 1990-1999'
      }[lang] || 'FIDIC Red & Yellow Books / Eurocodes EN 1990-1999',
      volume: {
        uk: '18 сторінок • 62 кошторисні позиції',
        pl: '18 stron • 62 pozycje kosztorysowe',
        de: '18 Seiten • 62 LV-Positionen',
        fr: '18 pages • 62 postes de métré',
        en: '18 pages • 62 line items'
      }[lang] || '18 pages • 62 line items',
      currency: isUk ? '₴' : isPl ? 'PLN' : isDe || isFr ? '€' : '$',
      totalAmount: isUk ? '482 650,00 ₴' : isPl ? '482 650,00 PLN' : isDe || isFr ? '482.650,00 €' : '$482,650.00',
      summaryText: {
        uk: 'Сформовано зведений локальний кошторис капітального ремонту нежитлової будівлі. Розраховано прямі витрати (матеріали, заробітна плата, експлуатація будівельних машин), загальновиробничі витрати за показниками Н38/Н39 та нормативний прибуток за показником Н42. Розрахунок виконано з формульними залежностями Excel (=SUM, =PRODUCT).',
        pl: 'Sporządzono pełny kosztorys inwestorski remontu kapitalnego budynku biurowo-usługowego. Obliczono koszty bezpośrednie R+M+S, koszty pośrednie Kp (65%) oraz zysk Z (11%) w oparciu o oficjalne katalogi norm KNR i cenniki SEKOCENBUD. Arkusz zawiera aktywne formuły Excel (=SUM, =ILOCZYN).',
        de: 'Vollständige Baukostenberechnung nach DIN 276 (Kostengruppen 300 Bauwerk und 400 Technische Anlagen). Aufstellung der Einzelkosten der Teilleistungen (EKT), Baustellengemeinkosten (BGK) und Wagnis & Gewinn mit aktiven Excel-Formeln.',
        fr: 'Établissement du devis quantitatif estimatif détaillé pour la rénovation d\'un bâtiment tertiaire. Calcul des déboursés secs (main-d\'œuvre, matériaux, matériels), frais généraux et marge bénéficiaire avec formules Excel actives.',
        en: 'Generated a comprehensive Bill of Quantities (BOQ) and Interim Payment Certificate (IPC) for commercial premises refurbishment. Direct labor, equipment hours, statutory overheads, and profit markups have been calculated with transparent formulaic dependencies.'
      }[lang] || 'Generated a comprehensive Bill of Quantities (BOQ) and Interim Payment Certificate (IPC) for commercial premises refurbishment.',
      rows: [
        {
          cipher: isUk ? 'РЕКН ЕН11-15-1' : isPl ? 'KNR 2-02 1101-01' : 'BOQ-ITEM-01',
          name: {
            uk: 'Улаштування стяжки цементної товщиною 30 мм (М150)',
            pl: 'Wykonanie posadzek cementowych z zatarciem na gładko grub. 30 mm',
            de: 'Estricharbeiten Zementestrich CT-C25-F4 Nenndicke 30 mm',
            fr: 'Chape fluide ciment épaisseur 30 mm finition lissée',
            en: 'Cementitious floor screed 30mm thickness grade C25/30'
          }[lang] || 'Cementitious floor screed 30mm thickness grade C25/30',
          qty: '450 m²',
          rate: isUk ? '250,00 ₴' : isPl ? '250,00 PLN' : isDe || isFr ? '250,00 €' : '$250.00',
          formula: isUk ? '=SUM(D4*E4) 112 500,00 ₴' : isPl ? '=SUM(D4*E4) 112 500,00 PLN' : isDe || isFr ? '=SUM(D4*E4) 112.500,00 €' : '=SUM(D4*E4) $112,500.00'
        },
        {
          cipher: isUk ? 'РЕКН Е15-54-2' : isPl ? 'KNR 4-01 1204-02' : 'BOQ-ITEM-02',
          name: {
            uk: 'Поліпшене шпаклювання та фарбування стін акрилом',
            pl: 'Dwukrotne malowanie farbami emulsyjnymi tynków wewnętrznych z gruntowaniem',
            de: 'Maler- und Lackierarbeiten Innenwandbeschichtung Dispersion Q3',
            fr: 'Peinture acrylique bicouche sur murs intérieurs avec couche d\'impression',
            en: 'Internal wall plaster skim coat & 2-coat acrylic emulsion finish'
          }[lang] || 'Internal wall plaster skim coat & 2-coat acrylic emulsion finish',
          qty: '1 200 m²',
          rate: isUk ? '72,00 ₴' : isPl ? '72,00 PLN' : isDe || isFr ? '72,00 €' : '$72.00',
          formula: isUk ? '=SUM(D5*E5) 86 400,00 ₴' : isPl ? '=SUM(D5*E5) 86 400,00 PLN' : isDe || isFr ? '=SUM(D5*E5) 86.400,00 €' : '=SUM(D5*E5) $86,400.00'
        }
      ],
      calcGrid: [
        { label: { uk: 'Прямі витрати (ПВ):', pl: 'Koszty bezpośrednie:', en: 'Direct Costs:' }[lang] || 'Direct Costs:', val: isUk ? '342 100,00 ₴' : isPl ? '342 100,00 PLN' : isDe || isFr ? '342.100,00 €' : '$342,100.00', col: 'text-blue-400' },
        { label: { uk: 'Загальновиробничі (ОПР):', pl: 'Koszty pośrednie Kp:', en: 'Overheads (Kp):' }[lang] || 'Overheads:', val: isUk ? '48 350,00 ₴' : isPl ? '48 350,00 PLN' : isDe || isFr ? '48.350,00 €' : '$48,350.00', col: 'text-amber-400' },
        { label: { uk: 'Кошторисний прибуток:', pl: 'Zysk kalkulacyjny Z:', en: 'Profit Margin (Z):' }[lang] || 'Profit Margin:', val: isUk ? '38 400,00 ₴' : isPl ? '38 400,00 PLN' : isDe || isFr ? '38.400,00 €' : '$38,400.00', col: 'text-emerald-400' },
        { label: { uk: 'ПДВ (20% / 23%):', pl: 'Podatek VAT (23%):', en: 'VAT / Tax:' }[lang] || 'VAT / Tax:', val: isUk ? '96 530,00 ₴' : isPl ? '111 009,50 PLN' : isDe || isFr ? '91.703,50 €' : '$96,530.00', col: 'text-purple-400' }
      ],
      actionPlan: {
        uk: [
          'Затвердити локальний кошторис № 02-01-04 як Додаток № 2 до будівельного підряду.',
          'Сформувати календарний графік фінансування та постачання будівельних матеріалів.',
          'Підписати щомісячні проміжні акти приймання виконаних робіт форми КБ-2в та довідки КБ-3.',
          'Експортувати формульну відомість ресурсів у формат Excel (.xlsx).'
        ],
        pl: [
          'Zatwierdzić kosztorys inwestorski jako Załącznik nr 2 do umowy o roboty budowlane.',
          'Ustalić harmonogram rzeczowo-finansowy oraz dostaw materiałów posadzkowych.',
          'Rozliczać etapy prac na podstawie Protokołów Odbioru Robót i Książki Obmiarów.',
          'Wyeksportować pełne zestawienie RMS do arkusza Excel (.xlsx).'
        ],
        de: [
          'Genehmigung der Baukostenberechnung nach DIN 276 als Vertragsbestandteil.',
          'Festlegung des Bauzeiten- und Zahlungsplans gemäß VOB/B § 16.',
          'Monatliche Abrechnung über kumulierte Abschlagsrechnungen.',
          'Export des Leistungsverzeichnisses im Excel- und GAEB-Format.'
        ],
        fr: [
          'Approuver le devis quantitatif estimatif comme annexe financière au marché de travaux.',
          'Établir le calendrier d\'avancement des travaux et d\'approvisionnement.',
          'Établir les situations mensuelles de travaux et les décomptes provisoires.',
          'Exporter le bordereau de prix unitaires au format Excel (.xlsx).'
        ],
        en: [
          'Approve the Bill of Quantities (BOQ) as Schedule 2 of the Works Contract.',
          'Establish the material procurement and disbursement milestone schedule.',
          'Issue monthly Interim Payment Certificates (IPC) pursuant to FIDIC Clause 14.',
          'Export the formulaic resource breakdown directly to Excel (.xlsx).'
        ]
      }[lang] || [
        'Approve the Bill of Quantities (BOQ) as Schedule 2 of the Works Contract.',
        'Establish the material procurement and disbursement milestone schedule.',
        'Issue monthly Interim Payment Certificates (IPC) pursuant to FIDIC Clause 14.',
        'Export the formulaic resource breakdown directly to Excel (.xlsx).'
      ]
    },

    satellite: {
      title: {
        uk: 'СУПУТНИКОВИЙ АГРО-ПАСПОРТ ПОЛЯ SENTINEL-2 (COPERNICUS L2A 10M)',
        pl: 'PASZPORT SATELITARNY DZIAŁKI ROLNEJ SENTINEL-2 (COPERNICUS L2A)',
        de: 'SATELLITEN-FELDGUTACHTEN SENTINEL-2 (COPERNICUS L2A 10M)',
        fr: 'PASSEPORT AGRONOMIQUE PAR SATELLITE SENTINEL-2 (COPERNICUS 10M)',
        it: 'PASSAPORTO COLTURALE SATELLITARE SENTINEL-2 (COPERNICUS L2A)',
        es: 'PASAPORTE AGRONÓMICO SATELITAL SENTINEL-2 (COPERNICUS L2A)',
        pt: 'PASSAPORTE AGRONÓMICO POR SATÉLITE SENTINEL-2 (COPERNICUS)',
        sv: 'SATELLITBASERAT ODLINGSPASS SENTINEL-2 (COPERNICUS L2A)',
        en: 'SATELLITE FIELD PASSPORT & VEGETATION INDEX AUDIT (SENTINEL-2 L2A)'
      }[lang] || 'SATELLITE FIELD PASSPORT & VEGETATION INDEX AUDIT (SENTINEL-2 L2A)',
      standard: 'Copernicus ESA • Sentinel-2 MultiSpectral Instrument (MSI) 10m',
      volume: {
        uk: '12 сторінок • 142.8 га обстеженої площі',
        pl: '12 stron • 142.8 ha zbadanej powierzchni',
        de: '12 Seiten • 142,8 ha untersuchte Fläche',
        fr: '12 pages • 142,8 ha surface analysée',
        en: '12 pages • 142.8 ha monitored area'
      }[lang] || '12 pages • 142.8 ha monitored area',
      summaryText: {
        uk: 'Проведено супутниковий моніторинг земельної ділянки кадастровий номер 4622783200:01:002:0145 (площа 142.8 га) за знімками Sentinel-2 (Copernicus L2A, 10м). Хмарність над об\'єктом становить 0.0%. Середній індекс вегетації посівів NDVI становить 0.74 (нормальний розвиток біомаси). Виявлено аномалію вегетації у секторі Б на площі 12.4 га (водний дефіцит).',
        pl: 'Przeprowadzono satelitarny monitoring działki ewidencyjnej nr 4622783200:01:002:0145 (powierzchnia 142.8 ha) w oparciu o dane Sentinel-2 (Copernicus L2A, 10m). Zachmurzenie: 0.0%. Średni wskaźnik wegetacji NDVI wynosi 0.74 (wysoka witalność upraw). Zidentyfikowano strefę stresu wodnego w sektorze B (12.4 ha).',
        de: 'Satellitengestützte Erdbeobachtung für das Flurstück Nr. 4622783200:01:002:0145 (142,8 ha) mittels Sentinel-2 (10m Auflösung). Bewölkung: 0,0%. Durchschnittlicher NDVI-Index: 0,74 (gute Vitalität). Trockenstresszone im Sektor B (12,4 ha) lokalisiert.',
        fr: 'Télédétection spatiale de la parcelle cadastrale n° 4622783200:01:002:0145 (142,8 ha) via Sentinel-2 (résolution 10m). Nébulosité: 0,0%. Indice de végétation moyen NDVI: 0,74. Anomalie hydrique détectée sur le secteur B (12,4 ha).',
        en: 'Executed multispectral satellite Earth observation monitoring for agricultural parcel #4622783200:01:002:0145 (142.8 ha) using Sentinel-2 L2A 10m imagery. Cloud cover: 0.0%. Average NDVI index stands at 0.74 (optimal biomass vitality). Vegetation stress anomaly identified in Sector B (12.4 ha).'
      }[lang] || 'Executed multispectral satellite Earth observation monitoring for agricultural parcel #4622783200:01:002:0145.',
      rows: [
        {
          zone: { uk: 'Сектор А (94.2 га)', pl: 'Sektor A (94.2 ha)', de: 'Sektor A (94,2 ha)', fr: 'Secteur A (94,2 ha)', en: 'Sector A (94.2 ha)' }[lang] || 'Sector A (94.2 ha)',
          indices: 'NDVI = 0.82 • NDWI = 0.46 • NDRE = 0.38',
          status: { uk: 'Оптимальний розвиток', pl: 'Optymalna wegetacja', de: 'Optimale Entwicklung', fr: 'Végétation optimale', en: 'Optimal Vitality' }[lang] || 'Optimal Vitality',
          recommendation: {
            uk: 'Стандартний плановий захист посівів.',
            pl: 'Standardowy plan zabiegów fungicydowych.',
            de: 'Standardmäßiger Pflanzenschutzplan.',
            fr: 'Poursuite du plan phytosanitaire standard.',
            en: 'Standard scheduled crop protection protocol.'
          }[lang] || 'Standard scheduled crop protection protocol.'
        },
        {
          zone: { uk: 'Сектор Б (12.4 га)', pl: 'Sektor B (12.4 ha)', de: 'Sektor B (12,4 ha)', fr: 'Secteur B (12,4 ha)', en: 'Sector B (12.4 ha)' }[lang] || 'Sector B (12.4 ha)',
          indices: 'NDVI = 0.28 • NDWI = 0.12 (Стрес)',
          status: { uk: 'Водний та азотний дефіцит', pl: 'Stres wodny i niedobór azotu', de: 'Trocken- & Stickstoffstress', fr: 'Stress hydrique et azoté', en: 'Water & Nitrogen Stress' }[lang] || 'Water & Nitrogen Stress',
          recommendation: {
            uk: 'Диференційоване внесення рідких добрив N-P-K.',
            pl: 'Zmienne dawkowanie nawozów N-P-K (VRA).',
            de: 'Teilflächenspezifische N-P-K Nachdüngung (VRA).',
            fr: 'Modulation intra-parcellaire de fertilisation N-P-K.',
            en: 'Variable rate application (VRA) of liquid N-P-K fertilizers.'
          }[lang] || 'Variable rate application (VRA) of liquid N-P-K fertilizers.'
        }
      ],
      calcGrid: [
        { label: { uk: 'Загальна площа поля:', pl: 'Powierzchnia całkowita:', en: 'Total Survey Area:' }[lang] || 'Total Survey Area:', val: '142.8 ha', col: 'text-blue-400' },
        { label: { uk: 'Середній індекс NDVI:', pl: 'Średni wskaźnik NDVI:', en: 'Mean NDVI Index:' }[lang] || 'Mean NDVI Index:', val: '0.74 (Healthy)', col: 'text-emerald-400' },
        { label: { uk: 'Вологість ґрунту NDWI:', pl: 'Wilgotność gleby NDWI:', en: 'Soil Moisture NDWI:' }[lang] || 'Soil Moisture NDWI:', val: '0.42 (Optimal)', col: 'text-teal-400' },
        { label: { uk: 'Зона азотного стресу:', pl: 'Strefa niedoboru azotu:', en: 'Nitrogen Stress Zone:' }[lang] || 'Nitrogen Stress Zone:', val: '8.7% (12.4 ha)', col: 'text-amber-400' }
      ],
      actionPlan: {
        uk: [
          'Передати контури сектора Б (12.4 га) на бортовий термінал обприскувача.',
          'Виконати диференційоване внесення рідких комплексних добрив N-P-K (VRA).',
          'Запланувати повторну супутникову зйомку Sentinel-2 через 5 днів для оцінки динаміки.',
          'Зберегти агро-паспорт поля у форматі Word (.docx) та карту в GeoTIFF.'
        ],
        pl: [
          'Wgrać mapę aplikacyjną sektora B (12.4 ha) do komputera pokładowego opryskiwacza.',
          'Przeprowadzić zmienne nawożenie dolistne N-P-K (VRA) na wyznaczonym obszarze.',
          'Zaplanować kolejny przelot satelity Sentinel-2 za 5 dni celem weryfikacji przyrostu biomasy.',
          'Wyeksportować paszport pola do formatu Word (.docx) oraz pliki rastrowe GeoTIFF.'
        ],
        de: [
          'Übertragung der Applikationskarte für Sektor B (12,4 ha) auf das Bordterminal des Traktors.',
          'Durchführung der teilflächenspezifischen N-P-K Flüssigdüngung (VRA).',
          'Folge-Satellitenüberflug in 5 Tagen zur Vegetationskontrolle terminieren.',
          'Export des Feldpasses als Word-Bericht (.docx) und GeoTIFF-Geodaten.'
        ],
        fr: [
          'Transférer la carte de modulation du secteur B (12,4 ha) sur la console du tracteur.',
          'Appliquer la fertilisation foliaire N-P-K à dose variable (VRA).',
          'Programmer une acquisition satellite de contrôle à J+5 pour évaluer la reprise végétative.',
          'Exporter le passeport parcellaire au format Word (.docx) et couches GeoTIFF.'
        ],
        en: [
          'Upload the Sector B (12.4 ha) prescription VRA map to the onboard tractor terminal.',
          'Execute targeted variable-rate foliar N-P-K fertilizer application.',
          'Schedule follow-up Sentinel-2 multispectral pass in 5 days to assess vegetation recovery.',
          'Export the full agronomic field passport in Word (.docx) and GeoTIFF formats.'
        ]
      }[lang] || [
        'Upload the Sector B (12.4 ha) prescription VRA map to the onboard tractor terminal.',
        'Execute targeted variable-rate foliar N-P-K fertilizer application.',
        'Schedule follow-up Sentinel-2 multispectral pass in 5 days to assess vegetation recovery.',
        'Export the full agronomic field passport in Word (.docx) and GeoTIFF formats.'
      ]
    },

    tax_cbam: {
      title: isPl
        ? 'PROTOKÓŁ WALIDACJI STRUKTURY E-FAKTURY KSEF FA(2), JPK_V7 I BIAŁEJ LISTY MF'
        : isUk
        ? 'ПРОТОКОЛ АУДИТУ ПДВ СМКОР & ДЕКЛАРАЦІЯ ВУГЛЕЦЕВОГО КОРИГУВАННЯ ЄС (CBAM)'
        : 'EU CBAM CARBON BORDER EMISSIONS & VAT STATUTORY AUDIT REPORT',
      standard: isPl
        ? 'Ustawa o VAT art. 106nd • Schemat XSD FA(2) • Rozporządzenie JPK_V7'
        : 'EU Regulation 2023/956 (CBAM) • EU ETS Directive 2003/87/EC',
      volume: {
        uk: '15 сторінок • 100% відповідність вимогам ЄС',
        pl: '15 stron • 100% zgodności ze schemą MF',
        de: '15 Seiten • 100% EU-Konformität',
        fr: '15 pages • 100% conformité UE',
        en: '15 pages • 100% EU ETS & CBAM Compliance'
      }[lang] || '15 pages • 100% EU ETS & CBAM Compliance',
      summaryText: isPl
        ? 'Przeprowadzono automatyczną walidację składniową i semantyczną pliku XML faktury ustrukturyzowanej KSeF w formacie FA(2). Zweryfikowano poprawność stawek VAT, status kontrahenta na Białej Liście Podatników VAT Ministerstwa Finansów oraz wymóg zastosowania Mechanizmu Podzielonej Płatności (MPP).'
        : isUk
        ? 'Виконано розрахунок прямих і непрямих питомих викидів парникових газів ($CO_2$ equivalent) для експорту металопродукції до країн Європейського Союзу відповідно до Регламенту (EU) 2023/956 (CBAM). Сформовано квартальну декларацію для перехідного реєстру CBAM Registry.'
        : 'Calculated specific direct (Scope 1: 1.42 t CO2e/t) and indirect (Scope 2: 0.42 t CO2e/t) embedded greenhouse gas emissions under EU Regulation 2023/956 (CBAM). Drafted transitional registry quarterly declaration with carbon certificate liability assessment.',
      rows: [
        {
          item: isPl ? 'Walidacja XML FA(2)' : 'CBAM Tariff Code CN 7208',
          status: isPl ? '✓ Poprawna (0 błędów składni)' : '1.84 t CO2e / tonne of steel',
          norm: isPl ? 'Weryfikacja ze schemą XSD Ministerstwa Finansów' : 'Regulation (EU) 2023/956 Annex IV default benchmarks',
          action: isPl ? 'Faktura gotowa do bezpiecznej wysyłki do bramki KSeF' : 'Verified transitional emission factor ready for EU CBAM registry filing'
        },
        {
          item: isPl ? 'Biała Lista Podatników VAT' : 'Embedded Indirect Emissions',
          status: isPl ? '✓ Rachunek bankowy w rejestrze' : '0.42 t CO2e / tonne (Electricity grid factor)',
          norm: isPl ? 'Elektroniczny wykaz podmiotów VAT prowadzony przez KAS' : 'EU ETS electricity carbon intensity benchmarks',
          action: isPl ? '100% prawo do odliczenia podatku naliczonego VAT 23%' : 'Ready for auditor QES digital signature and declaration export'
        }
      ],
      calcGrid: [
        { label: isPl ? 'Wartość netto faktury:' : 'Direct Scope 1 Emissions:', val: isPl ? '100 000,00 PLN' : '1.42 t CO2e / t', col: 'text-blue-400' },
        { label: isPl ? 'Podatek VAT (23%):' : 'Indirect Scope 2 Emissions:', val: isPl ? '23 000,00 PLN' : '0.42 t CO2e / t', col: 'text-amber-400' },
        { label: isPl ? 'Status Białej Listy:' : 'EU ETS Benchmark Gap:', val: isPl ? 'Aktywny (0 ryzyk)' : '+0.12 t CO2e / t', col: 'text-emerald-400' },
        { label: isPl ? 'Split Payment (MPP):' : 'CBAM Certificate Cost:', val: isPl ? 'Obowiązkowy (>15k)' : '€14.80 / tonne', col: 'text-purple-400' }
      ],
      actionPlan: {
        uk: [
          'Подати таблицю даних платника податку з кодами УКТ ЗЕД до Головного управління ДПС.',
          'Внести розрахунок питомих викидів CO2 до квартальної декларації CBAM перехідного періоду.',
          'Сформувати супровідний пакет первинних документів (ТТН, акти, банківські виписки).',
          'Зафіксувати цифровий підпис КЕП на декларації перед відправкою до митних органів ЄС.'
        ],
        pl: [
          'Wysłać zwalidowany plik XML faktury do produkcyjnej bramki KSeF API.',
          'Pobrać Urzędowe Poświadczenie Odbioru (UPO) z nadanym unikalnym numerem KSeF.',
          'Wprowadzić zapisy faktury do ewidencji JPK_V7M za bieżący okres rozliczeniowy.',
          'Zachować certyfikat walidacji Białej Listy w archiwum podatkowym spółki.'
        ],
        de: [
          'Übermittlung der CBAM-Quartalserklärung an das CBAM-Übergangsregister der EU.',
          'Dokumentation der direkten und indirekten Emissionen nach Anhang IV der Verordnung (EU) 2023/956.',
          'Abstimmung mit den zugelassenen CBAM-Anmeldern der europäischen Importeure.',
          'Digitale QES-Archivierung des Prüfberichts für steuerliche Betriebsprüfungen.'
        ],
        fr: [
          'Soumettre la déclaration trimestrielle CBAM au registre transitoire de la Commission européenne.',
          'Justifier les facteurs d\'émission spécifiques Scope 1 et Scope 2 de la production.',
          'Transmettre le certificat de conformité aux importateurs européens pour dédouanement.',
          'Archiver le rapport d\'audit avec signature électronique qualifiée.'
        ],
        en: [
          'Submit the transitional CBAM quarterly emissions report to the European Commission registry.',
          'Validate specific Scope 1 direct and Scope 2 electricity grid carbon intensity factors.',
          'Provide validated emissions certificates to EU customs brokers and authorized declarants.',
          'Archive the QES-signed tax compliance statement for statutory audit defense.'
        ]
      }[lang] || [
        'Submit the transitional CBAM quarterly emissions report to the European Commission registry.',
        'Validate specific Scope 1 direct and Scope 2 electricity grid carbon intensity factors.',
        'Provide validated emissions certificates to EU customs brokers and authorized declarants.',
        'Archive the QES-signed tax compliance statement for statutory audit defense.'
      ]
    }
  };

  const currentDoc = reportData[activeDocType];

  return (
    <div className="w-full bg-slate-900 border border-slate-700/80 rounded-2xl overflow-hidden shadow-2xl space-y-5 p-4 sm:p-6 md:p-7 text-left my-5">
      
      {/* Top Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 border-b border-slate-800 pb-4">
        <div className="space-y-1.5">
          <div className="flex items-center gap-2.5">
            <span className="p-2 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 text-emerald-300 rounded-xl border border-emerald-400/30 shrink-0">
              <Eye className="w-5 h-5" />
            </span>
            <h3 className="text-xs sm:text-sm md:text-base font-black text-white uppercase tracking-wide">
              {ui.title}
            </h3>
          </div>
          <p className="text-xs text-slate-300 font-mono leading-relaxed">
            {ui.subtitle}
          </p>
        </div>

        {/* Speed & Compliance Badge */}
        <div className="flex items-center gap-2 bg-emerald-950/80 border border-emerald-500/40 px-3.5 py-2 rounded-xl shrink-0 self-start lg:self-center shadow-md">
          <Sparkles className="w-4 h-4 text-emerald-400 animate-pulse" />
          <span className="text-[11px] font-extrabold text-emerald-300 font-mono tracking-wider">
            {ui.badgeGenerated}
          </span>
        </div>
      </div>

      {/* Main Document Selector Tabs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
        <button
          type="button"
          onClick={() => { setActiveDocType('contract'); setActiveSection('details'); }}
          className={`p-3 rounded-xl border text-xs font-bold font-mono transition-all flex flex-col sm:flex-row items-center gap-2.5 cursor-pointer text-left ${
            activeDocType === 'contract'
              ? 'bg-blue-600/30 border-blue-400 text-white shadow-lg ring-1 ring-blue-400/50'
              : 'bg-slate-800/60 border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white'
          }`}
        >
          <div className="p-2 bg-blue-500/20 text-blue-400 rounded-lg shrink-0">
            <FileText className="w-4 h-4" />
          </div>
          <div className="truncate">
            <div className="font-extrabold text-[11px] sm:text-xs">
              {ui.tabContract}
            </div>
            <div className="text-[10px] text-slate-400 font-normal">Word (.docx)</div>
          </div>
        </button>

        <button
          type="button"
          onClick={() => { setActiveDocType('estimate'); setActiveSection('calculations'); }}
          className={`p-3 rounded-xl border text-xs font-bold font-mono transition-all flex flex-col sm:flex-row items-center gap-2.5 cursor-pointer text-left ${
            activeDocType === 'estimate'
              ? 'bg-emerald-600/30 border-emerald-400 text-white shadow-lg ring-1 ring-emerald-400/50'
              : 'bg-slate-800/60 border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white'
          }`}
        >
          <div className="p-2 bg-emerald-500/20 text-emerald-400 rounded-lg shrink-0">
            <FileSpreadsheet className="w-4 h-4" />
          </div>
          <div className="truncate">
            <div className="font-extrabold text-[11px] sm:text-xs">
              {ui.tabEstimate}
            </div>
            <div className="text-[10px] text-slate-400 font-normal">Excel (.xlsx)</div>
          </div>
        </button>

        <button
          type="button"
          onClick={() => { setActiveDocType('satellite'); setActiveSection('details'); }}
          className={`p-3 rounded-xl border text-xs font-bold font-mono transition-all flex flex-col sm:flex-row items-center gap-2.5 cursor-pointer text-left ${
            activeDocType === 'satellite'
              ? 'bg-indigo-600/30 border-indigo-400 text-white shadow-lg ring-1 ring-indigo-400/50'
              : 'bg-slate-800/60 border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white'
          }`}
        >
          <div className="p-2 bg-indigo-500/20 text-indigo-400 rounded-lg shrink-0">
            <Satellite className="w-4 h-4" />
          </div>
          <div className="truncate">
            <div className="font-extrabold text-[11px] sm:text-xs">
              {ui.tabSat}
            </div>
            <div className="text-[10px] text-slate-400 font-normal">GIS Sentinel-2</div>
          </div>
        </button>

        <button
          type="button"
          onClick={() => { setActiveDocType('tax_cbam'); setActiveSection('details'); }}
          className={`p-3 rounded-xl border text-xs font-bold font-mono transition-all flex flex-col sm:flex-row items-center gap-2.5 cursor-pointer text-left ${
            activeDocType === 'tax_cbam'
              ? 'bg-amber-600/30 border-amber-400 text-white shadow-lg ring-1 ring-amber-400/50'
              : 'bg-slate-800/60 border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white'
          }`}
        >
          <div className="p-2 bg-amber-500/20 text-amber-400 rounded-lg shrink-0">
            <ShieldCheck className="w-4 h-4" />
          </div>
          <div className="truncate">
            <div className="font-extrabold text-[11px] sm:text-xs">
              {ui.tabTax}
            </div>
            <div className="text-[10px] text-slate-400 font-normal">XML / PDF</div>
          </div>
        </button>
      </div>

      {/* Document Sub-Navigation (Sections) */}
      <div className="flex items-center gap-2 border-b border-slate-800 pb-2 overflow-x-auto scrollbar-none text-xs font-mono">
        <button
          type="button"
          onClick={() => setActiveSection('summary')}
          className={`px-3.5 py-1.5 rounded-lg transition-all cursor-pointer font-bold shrink-0 ${
            activeSection === 'summary'
              ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-400/40'
              : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
          }`}
        >
          {ui.sectionSummary}
        </button>

        <button
          type="button"
          onClick={() => setActiveSection('details')}
          className={`px-3.5 py-1.5 rounded-lg transition-all cursor-pointer font-bold shrink-0 ${
            activeSection === 'details'
              ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-400/40'
              : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
          }`}
        >
          {ui.sectionDetails}
        </button>

        <button
          type="button"
          onClick={() => setActiveSection('calculations')}
          className={`px-3.5 py-1.5 rounded-lg transition-all cursor-pointer font-bold shrink-0 ${
            activeSection === 'calculations'
              ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-400/40'
              : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
          }`}
        >
          {ui.sectionCalculations}
        </button>

        <button
          type="button"
          onClick={() => setActiveSection('action_plan')}
          className={`px-3.5 py-1.5 rounded-lg transition-all cursor-pointer font-bold shrink-0 ${
            activeSection === 'action_plan'
              ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-400/40'
              : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800'
          }`}
        >
          {ui.sectionPlan}
        </button>
      </div>

      {/* Live Realistic Report Sheet Canvas */}
      <div className="bg-slate-950 border border-slate-800 rounded-xl p-4 sm:p-6 md:p-7 font-mono text-slate-200 space-y-6 shadow-inner max-h-[560px] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-900">
        
        {/* Document Header Banner */}
        <div className="border-b-2 border-slate-700 pb-4 space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-[11px] text-slate-400">
            <div className="flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-ping" />
              <span className="font-extrabold text-slate-300 uppercase tracking-wide">
                {currentDoc.title}
              </span>
            </div>
            <span className="text-emerald-400 bg-emerald-950/80 px-2.5 py-1 rounded border border-emerald-500/40 font-bold self-start sm:self-auto">
              ✓ 100% COMPLIANT
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-slate-900/90 p-3 rounded-lg border border-slate-800 text-[10.5px]">
            <div>
              <span className="text-slate-400 block">{isUk ? 'Дата генерації:' : isPl ? 'Data:' : 'Date:'}</span>
              <strong className="text-slate-200">26.08.2026</strong>
            </div>
            <div>
              <span className="text-slate-400 block">{isUk ? 'Нормативна база:' : isPl ? 'Norma:' : 'Standard:'}</span>
              <strong className="text-blue-400">{currentDoc.standard}</strong>
            </div>
            <div>
              <span className="text-slate-400 block">{isUk ? 'Обсяг:' : isPl ? 'Objętość:' : 'Volume:'}</span>
              <strong className="text-emerald-400">{currentDoc.volume}</strong>
            </div>
            <div>
              <span className="text-slate-400 block">{isUk ? 'Статус:' : isPl ? 'Status:' : 'Status:'}</span>
              <strong className="text-emerald-300">{isUk ? 'Без ризиків' : isPl ? 'Bezpieczny' : 'Verified Clean'}</strong>
            </div>
          </div>
        </div>

        {/* 1. SUMMARY SECTION */}
        {activeSection === 'summary' && (
          <div className="space-y-4 animate-fade-in text-xs">
            <div className="p-4 bg-blue-950/40 border border-blue-500/30 rounded-xl space-y-2">
              <h4 className="text-xs font-black text-blue-300 uppercase tracking-wide flex items-center gap-2">
                <Bookmark className="w-4 h-4 text-blue-400" />
                <span>{ui.sectionSummary}</span>
              </h4>
              <p className="text-slate-200 leading-relaxed text-[11.5px]">
                {currentDoc.summaryText}
              </p>
            </div>
          </div>
        )}

        {/* 2. RISK & CLAUSE MATRIX SECTION */}
        {activeSection === 'details' && (
          <div className="space-y-4 animate-fade-in text-xs">
            <div className="flex items-center justify-between">
              <h4 className="font-extrabold text-white text-xs uppercase tracking-wide flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400" />
                <span>{ui.sectionDetails}</span>
              </h4>
              <span className="text-[10px] text-slate-400 font-mono">100% statutory safety</span>
            </div>

            {/* Matrix Table */}
            <div className="overflow-x-auto border border-slate-800 rounded-xl bg-slate-900/60">
              <table className="w-full text-[11px] font-mono">
                <thead>
                  <tr className="bg-slate-900 text-slate-400 text-left border-b border-slate-800">
                    <th className="p-3">№ / {isUk ? 'Пункт' : isPl ? 'Pozycja' : 'Clause'}</th>
                    <th className="p-3">{isUk ? 'Зміст / Виявлена загроза' : isPl ? 'Treść / Zagrożenie' : 'Subject / Issue'}</th>
                    <th className="p-3">{isUk ? 'Аналіз та норма' : isPl ? 'Analiza i podstawa' : 'Statutory Analysis'}</th>
                    <th className="p-3">{isUk ? 'Виправлена редакція ШІ' : isPl ? 'Wersja poprawiona AI' : 'AI Redline / Solution'}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {activeDocType === 'contract' && (reportData.contract.rows as any[]).map((r, idx) => (
                    <tr key={idx} className="hover:bg-slate-900/80 transition-colors">
                      <td className="p-3 font-extrabold text-amber-300 align-top">{r.clause}</td>
                      <td className="p-3 text-red-300 font-bold align-top">{r.initial}</td>
                      <td className="p-3 text-slate-300 align-top text-[10.5px]">{r.analysis}</td>
                      <td className="p-3 text-emerald-300 font-bold align-top">{r.redline}</td>
                    </tr>
                  ))}

                  {activeDocType === 'estimate' && (reportData.estimate.rows as any[]).map((r, idx) => (
                    <tr key={idx} className="hover:bg-slate-900/80 transition-colors">
                      <td className="p-3 font-bold text-blue-300 align-top">{r.cipher}</td>
                      <td className="p-3 text-slate-300 align-top">{r.name}</td>
                      <td className="p-3 text-slate-400 align-top">{r.qty} • {r.rate}</td>
                      <td className="p-3 text-emerald-400 font-bold align-top text-right font-mono">{r.formula}</td>
                    </tr>
                  ))}

                  {activeDocType === 'satellite' && (reportData.satellite.rows as any[]).map((r, idx) => (
                    <tr key={idx} className="hover:bg-slate-900/80 transition-colors">
                      <td className="p-3 font-bold text-emerald-400 align-top">{r.zone}</td>
                      <td className="p-3 text-emerald-300 font-bold align-top">{r.indices}</td>
                      <td className="p-3 text-slate-300 align-top">{r.status}</td>
                      <td className="p-3 text-emerald-300 font-bold align-top">{r.recommendation}</td>
                    </tr>
                  ))}

                  {activeDocType === 'tax_cbam' && (reportData.tax_cbam.rows as any[]).map((r, idx) => (
                    <tr key={idx} className="hover:bg-slate-900/80 transition-colors">
                      <td className="p-3 font-bold text-blue-300 align-top">{r.item}</td>
                      <td className="p-3 text-emerald-300 font-bold align-top">{r.status}</td>
                      <td className="p-3 text-slate-300 align-top">{r.norm}</td>
                      <td className="p-3 text-emerald-300 font-bold align-top">{r.action}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 3. CALCULATIONS & FORMULAS SECTION */}
        {activeSection === 'calculations' && (
          <div className="space-y-4 animate-fade-in text-xs">
            <div className="p-4 bg-emerald-950/40 border border-emerald-500/30 rounded-xl space-y-2">
              <h4 className="text-xs font-black text-emerald-300 uppercase tracking-wide flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Table className="w-4 h-4 text-emerald-400" />
                  <span>{ui.sectionCalculations}</span>
                </span>
                <span className="text-emerald-400 font-mono font-bold text-sm">
                  {activeDocType === 'estimate' ? reportData.estimate.totalAmount : '100% Calculated'}
                </span>
              </h4>
              <p className="text-slate-300 text-[11px]">
                {isUk ? 'Усі розрахунки формуються з живими математичними залежностями та експортуються у формульний Excel (.xlsx).' : isPl ? 'Wszystkie obliczenia zawierają aktywne formuły Excel (=SUM, =ILOCZYN) bez statycznego wpisywania sum.' : 'All calculations contain active formulas (=SUM, =PRODUCT) with zero hardcoded values.'}
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              {((currentDoc as any).calcGrid || reportData.estimate.calcGrid).map((c: any, idx: number) => (
                <div key={idx} className="p-3 bg-slate-900 border border-slate-800 rounded-xl">
                  <span className="text-[10px] text-slate-400 block">{c.label}</span>
                  <span className={`text-sm font-extrabold ${c.col || 'text-white'}`}>{c.val}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 4. ACTION PLAN SECTION */}
        {activeSection === 'action_plan' && (
          <div className="space-y-4 animate-fade-in text-xs">
            <div className="p-4 bg-purple-950/40 border border-purple-500/30 rounded-xl space-y-2">
              <h4 className="text-xs font-black text-purple-300 uppercase tracking-wide flex items-center gap-2">
                <Award className="w-4 h-4 text-purple-400" />
                <span>{ui.sectionPlan}</span>
              </h4>
              <p className="text-slate-300 text-[11px]">
                {isUk ? 'ШІ-Агент формує супровідні листи та протоколи розбіжностей для негайного застосування.' : isPl ? 'AI generuje gotowy protokół rozbieżności i pismo przewodnie do natychmiastowego podpisu.' : 'AI Agent drafts ready-to-sign departure statements and official cover letters.'}
              </p>
            </div>

            <ol className="list-decimal pl-5 space-y-2 text-slate-300 text-[11.5px] leading-relaxed">
              {((currentDoc as any).actionPlan || reportData.contract.actionPlan).map((step: string, idx: number) => (
                <li key={idx}>
                  <strong>{step}</strong>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Document Footer & Signatures */}
        <div className="border-t border-slate-800 pt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-[10.5px] text-slate-400">
          <div className="flex items-center gap-2">
            <UserCheck className="w-4 h-4 text-emerald-400" />
            <span>QES Digital Signature: <strong>DEEP-RESEARCH-HUB-QES-2026</strong></span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-emerald-400 font-bold">100% Export: Word (.docx) & Excel (.xlsx)</span>
          </div>
        </div>

      </div>

      {/* Bottom Action Footer */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2 border-t border-slate-800">
        <div className="flex items-center gap-2 text-xs text-slate-300">
          <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
          <span className="font-bold">
            {ui.freeRunBanner}
          </span>
        </div>

        <a
          href="#login-card-section"
          className="px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-black text-xs uppercase rounded-xl transition-all shadow-lg flex items-center justify-center gap-2 shrink-0 animate-pulse cursor-pointer"
        >
          <span>{ui.tryNowBtn}</span>
          <ArrowRight className="w-4 h-4" />
        </a>
      </div>

    </div>
  );
}
